import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject } from 'rxjs';
import { DialogService } from './dialog.service';
import { ContactService, WhatsAppConsentSource } from './contact.service';
import {
  PhoneSetupDialogComponent,
  PhoneSetupMode,
  PhoneSetupResult
} from '../../modules/shared/components/phone-setup-dialog/phone-setup-dialog.component';

const SNOOZE_KEY = 'contemplate.phoneSetupSnoozeUntil';
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;

export interface OpenPhoneSetupOptions {
  mode?: PhoneSetupMode;
  source: WhatsAppConsentSource;
  currentMasked?: string | null;
  legacyE164?: string | null;
  countryIso2?: string | null;
  allowSnooze?: boolean;
  pending?: string[];
}

@Injectable({ providedIn: 'root' })
export class PhoneSetupService {
  private open = false;
  private readonly dialogOpenSubject = new BehaviorSubject<boolean>(false);
  /** True while the setup dialog is on screen (the floating checklist hides). */
  readonly dialogOpen$ = this.dialogOpenSubject.asObservable();
  private readonly completedSubject = new Subject<void>();
  /** Emits after a number is verified through the dialog (any entry point). */
  readonly completed$ = this.completedSubject.asObservable();
  /** Once per login session (page lifetime); reset by `resetPrompt()` on logout. */
  private promptedThisSession = false;

  constructor(
    private dialogs: DialogService,
    private contact: ContactService
  ) {}

  /** For inline verification flows (onboarding): same signal the dialog emits on success. */
  notifyVerified(): void {
    this.completedSubject.next();
  }

  /** Opens the dialog and resolves with its result (`{}` if dismissed). */
  openDialog(options: OpenPhoneSetupOptions): Promise<PhoneSetupResult> {
    if (this.open) return Promise.resolve({});
    this.open = true;
    this.dialogOpenSubject.next(true);
    const ref = this.dialogs.open(PhoneSetupDialogComponent, {
      mode: 'setup',
      ...options
    });
    return firstValueFrom(ref.afterClosed$).then(
      (res) => {
        this.open = false;
        this.dialogOpenSubject.next(false);
        const result = (res ?? {}) as PhoneSetupResult;
        if (result.verified) this.completedSubject.next();
        return result;
      },
      () => {
        this.open = false;
        this.dialogOpenSubject.next(false);
        return {};
      }
    );
  }

  /** Change flow: loads the masked number first so the dialog can show it. */
  async openChange(source: WhatsAppConsentSource): Promise<PhoneSetupResult> {
    let status;
    try {
      status = await firstValueFrom(this.contact.getStatus());
    } catch {
      status = null;
    }
    return this.openDialog({
      mode: status?.whatsapp_verified ? 'change' : 'setup',
      source,
      currentMasked: status?.whatsapp_e164_masked ?? null,
      countryIso2: status?.country_iso2 ?? null,
      legacyE164: status?.legacy_whatsapp_e164 ?? null
    });
  }

  /** Existing-user prompt. Safe to call repeatedly; shows at most once per session. */
  async promptIfNeeded(): Promise<void> {
    if (this.promptedThisSession || this.isSnoozed()) return;
    this.promptedThisSession = true;
    let status;
    try {
      status = await firstValueFrom(this.contact.getStatus());
    } catch {
      return; // never block the app on this
    }
    if (!status?.needs_setup) return;
    const res = await this.openDialog({
      mode: 'setup',
      source: 'setup_prompt',
      allowSnooze: true,
      countryIso2: status.country_iso2,
      legacyE164: status.legacy_whatsapp_e164,
      currentMasked: status.whatsapp_e164_masked
    });
    if (res.snoozed) this.snooze();
  }

  resetPrompt(): void {
    this.promptedThisSession = false;
  }

  private isSnoozed(): boolean {
    try {
      const until = Number(localStorage.getItem(SNOOZE_KEY));
      return Number.isFinite(until) && until > Date.now();
    } catch {
      return false;
    }
  }

  private snooze(): void {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    } catch {
      /* storage unavailable: prompt returns next session */
    }
  }
}
